// Google Analytics 4 — rastreamento de jornada do usuário DENTRO do produto (app).
//
// Design:
// - ID configurável por env de BUILD: VITE_GA_MEASUREMENT_ID. Vazio ⇒ no-op total
//   (gtag nunca carrega; track()/trackPageView() viram funções vazias).
// - Guard de ambiente: só rastreia em PRODUÇÃO (hostname === 'app.bzapper.com.br').
//   localhost/preview/qualquer outro host = no-op (evita poluir o stream de prod).
// - gtag.js é carregado DINAMICAMENTE (não no index.html), só quando há ID e é prod.
// - anonymize_ip ligado; SPA: nós controlamos os page_view manualmente (send_page_view: false).
//
// TODO(consent): integrar com Google Consent Mode. O banner de consentimento vive no
//   SITE de marketing; aqui no app é analytics de PRODUTO (usuário já autenticado). Quando
//   houver Consent Mode global, chamar gtag('consent', 'default'/'update', {...}) antes do
//   primeiro evento e respeitar a escolha do usuário.

const MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID as string | undefined;
const PROD_HOSTNAME = 'app.bzapper.com.br';

type GtagArgs = [string, ...unknown[]];

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: GtagArgs) => void;
  }
}

let initialized = false;

/** Só rastreia quando há ID configurado E estamos no host de produção do app. */
function isEnabled(): boolean {
  if (!MEASUREMENT_ID) return false;
  if (typeof window === 'undefined') return false;
  return window.location.hostname === PROD_HOSTNAME;
}

/**
 * Carrega o gtag.js dinamicamente e configura o GA4. Idempotente e seguro:
 * vira no-op quando não há ID ou não estamos em produção.
 */
export function initAnalytics(): void {
  if (initialized || !isEnabled()) return;
  initialized = true;

  const id = MEASUREMENT_ID as string;
  window.dataLayer = window.dataLayer || [];
  // gtag empurra `arguments` cru no dataLayer (assinatura oficial do GA4).
  const gtag: (...args: GtagArgs) => void = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer!.push(arguments);
  };
  window.gtag = gtag;

  gtag('js', new Date());
  // send_page_view: false → a SPA dispara page_view a cada navegação do React Router.
  gtag('config', id, { anonymize_ip: true, send_page_view: false });

  const s = document.createElement('script');
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`;
  document.head.appendChild(s);
}

/** Evento de jornada de produto. No-op quando GA está desligado. */
export function track(event: string, params?: Record<string, unknown>): void {
  if (!isEnabled() || !window.gtag) return;
  window.gtag('event', event, params ?? {});
}

/** page_view manual (SPA). Chamado a cada navegação do React Router. */
export function trackPageView(path: string, title?: string): void {
  if (!isEnabled() || !window.gtag) return;
  window.gtag('event', 'page_view', {
    page_path: path,
    page_title: title ?? document.title,
    page_location: window.location.href,
  });
}
